export interface ScaleConfig {
    unit: string;
    domain: [number, number];
    yAxisId: string;
    orientation: 'left' | 'right';
    color: string;
    colorPalette: string[]; // Add color palette for multiple lines of same unit
}

export interface ScaleConfigWithTheme extends Omit<ScaleConfig, 'color' | 'colorPalette'> {
    lightMode: {
        color: string;
        colorPalette: string[];
    };
    darkMode: {
        color: string;
        colorPalette: string[];
    };
}

export const SCALE_CONFIGS: Record<string, ScaleConfigWithTheme> = {
    '°F': {
        unit: '°F',
        domain: [34, 100],
        yAxisId: '°F',
        orientation: 'left',
        lightMode: {
            color: '#dc2626', // Red base - darker for light mode
            colorPalette: ['#be123c', '#dc2626', '#b91c1c', '#991b1b'] // Darker reds for light mode
        },
        darkMode: {
            color: '#ef4444', // Red base - more saturated for dark mode
            colorPalette: ['#f97316', '#ef4444', '#dc2626', '#ea580c'] // More saturated reds/oranges for dark mode
        }
    },
    '°C': {
        unit: '°C',
        domain: [1, 38],
        yAxisId: '°C',
        orientation: 'left',
        lightMode: {
            color: '#dc2626', // Red base - darker for light mode
            colorPalette: ['#be123c', '#dc2626', '#b91c1c', '#991b1b'] // Darker reds for light mode
        },
        darkMode: {
            color: '#ef4444', // Red base - more saturated for dark mode
            colorPalette: ['#f97316', '#ef4444', '#dc2626', '#ea580c'] // More saturated reds/oranges for dark mode
        }
    },
    '%': {
        unit: '%',
        domain: [0, 100],
        yAxisId: 'percentage',
        orientation: 'right',
        lightMode: {
            color: '#2563eb', // Blue base - darker for light mode
            colorPalette: ['#1e40af', '#1d4ed8', '#0891b2', '#0e7490', '#164e63'] // Darker blues/cyans for light mode
        },
        darkMode: {
            color: '#3b82f6', // Blue base - more saturated for dark mode
            colorPalette: ['#2563eb', '#3b82f6', '#0ea5e9', '#0284c7', '#1e40af'] // More saturated blues/cyans for dark mode
        }
    },
    'ppm': {
        unit: 'ppm',
        domain: [250, 1000],
        yAxisId: 'ppm',
        orientation: 'left',
        lightMode: {
            color: '#4b5563', // Neutral gray base - darker for light mode
            colorPalette: ['#374151', '#4b5563', '#475569', '#334155', '#1e293b'] // Darker grays for light mode
        },
        darkMode: {
            color: '#6b7280', // Neutral gray base - less washed out for dark mode
            colorPalette: ['#9ca3af', '#6b7280', '#64748b', '#78716c', '#71717a'] // More saturated grays for dark mode
        }
    },
    'hours': {
        unit: 'hours',
        domain: [0, 24],
        yAxisId: 'hours',
        orientation: 'right',
        lightMode: {
            color: '#059669', // Green base - darker for light mode
            colorPalette: ['#047857', '#059669', '#15803d', '#166534', '#14532d'] // Darker greens for light mode
        },
        darkMode: {
            color: '#10b981', // Green base - more saturated for dark mode
            colorPalette: ['#22c55e', '#10b981', '#14b8a6', '#059669', '#16a34a'] // More saturated greens for dark mode
        }
    },
    'sucrose': {
        unit: 'sucrose',
        domain: [0, 2],
        yAxisId: 'Sucrose',
        orientation: 'right',
        lightMode: {
            color: '#7c3aed', // Purple base - darker for light mode
            colorPalette: ['#6d28d9', '#7c3aed', '#6b21a8', '#581c87', '#a21caf'] // Darker purples/magentas for light mode
        },
        darkMode: {
            color: '#8b5cf6', // Purple base - more saturated for dark mode
            colorPalette: ['#a855f7', '#8b5cf6', '#9333ea', '#7c3aed', '#c026d3'] // More saturated purples/magentas for dark mode
        }
    },
    'glucose': {
        unit: 'glucose',
        domain: [0, .2],
        yAxisId: 'Glucose',
        orientation: 'right',
        lightMode: {
            color: '#059669', // Green base - darker for light mode
            colorPalette: ['#047857', '#059669', '#15803d', '#166534', '#14532d'] // Darker greens for light mode
        },
        darkMode: {
            color: '#10b981', // Green base - more saturated for dark mode
            colorPalette: ['#22c55e', '#10b981', '#14b8a6', '#059669', '#16a34a'] // More saturated greens for dark mode
        }
    },
    'deltaT': {
        unit: 'deltaT',
        domain: [0, 6],
        yAxisId: 'Delta T',
        orientation: 'left',
        lightMode: {
            color: '#d97706', // Yellow base - darker for light mode
            colorPalette: ['#b45309', '#d97706', '#92400e', '#78350f', '#c2410c'] // Darker yellows/oranges for light mode
        },
        darkMode: {
            color: '#f59e0b', // Yellow base - more saturated for dark mode
            colorPalette: ['#eab308', '#f59e0b', '#fbbf24', '#facc15', '#f97316'] // More saturated yellows/oranges for dark mode
        }
    }
};

export const getScaleConfig = (unit: string, mode: 'dark' | 'light' = 'dark'): ScaleConfig => {
    const configWithTheme = SCALE_CONFIGS[unit];
    
    if (configWithTheme) {
        const themeColors = mode === 'dark' ? configWithTheme.darkMode : configWithTheme.lightMode;
        return {
            unit: configWithTheme.unit,
            domain: configWithTheme.domain,
            yAxisId: configWithTheme.yAxisId,
            orientation: configWithTheme.orientation,
            color: themeColors.color,
            colorPalette: themeColors.colorPalette
        };
    }
    
    // Default fallback
    const defaultColors = mode === 'dark' 
        ? { color: '#6b7280', colorPalette: ['#9ca3af', '#6b7280', '#64748b', '#78716c', '#71717a'] }
        : { color: '#4b5563', colorPalette: ['#374151', '#4b5563', '#475569', '#334155', '#1e293b'] };
    
    return {
        unit: unit,
        domain: [0, 100],
        yAxisId: 'default',
        orientation: 'left',
        color: defaultColors.color,
        colorPalette: defaultColors.colorPalette
    };
};

export const groupMetricsByScale = (selectedMetrics: string[], getMetricInfo: (key: string) => any) => {
    const scaleGroups = new Map<string, string[]>();

    selectedMetrics.forEach(metric => {
        const metricInfo = getMetricInfo(metric);
        const unit = metricInfo.unit || 'default';

        if (!scaleGroups.has(unit)) {
            scaleGroups.set(unit, []);
        }
        scaleGroups.get(unit)!.push(metric);
    });

    return scaleGroups;
};

// Helper function to get the appropriate color for a metric within its unit group
export const getMetricColor = (metric: string, selectedMetrics: string[], getMetricInfo: (key: string) => any, mode: 'dark' | 'light' = 'dark'): string => {
    const metricInfo = getMetricInfo(metric);
    const scaleConfig = getScaleConfig(metricInfo.unit, mode);

    // Get all metrics with the same unit
    const sameUnitMetrics = selectedMetrics.filter(m => getMetricInfo(m).unit === metricInfo.unit);

    // Find the index of this metric within its unit group
    const indexInGroup = sameUnitMetrics.indexOf(metric);

    // Return the appropriate color from the palette
    return scaleConfig.colorPalette[indexInGroup % scaleConfig.colorPalette.length];
};